// External imports
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// Internal imports
import {
    FormContainer,
    LeftPanel,
    LeftPanelInsideContainer,
    Title,
    LogoImage,
    FormSection,
    RightPanel,
    LanguageSectionWrapper,
    LanguageHeaderEmptyArea,
    Heading,
} from "./LoginPage.styles";
import { LoginForm, FormData } from "./FormComponent/FormComponent";
import { MaintenanceBanner } from "../../components/maintenance-banner/MaintenanceBanner";
import { ModalRenderer } from "../../components/header/ModalRenderer";
import RolePicker from "./RolePicker/RolePicker";
import NoPermission from "../../components/no-permission/NoPermission";
import { AdminRole, resolveAdminAccess } from "./adminAccess";
// @ts-ignore
import myImage from "../../../assets/outside.jpg";
// @ts-ignore
import logoImage from "../../assets/clinical_metrics.png";

// Dependencies injections for the login page
import {
    LoginUserType,
    LoginResponse,
} from "../../core/ServerApiLayer/VOne/auth/login";
import { ChooseRoleType } from "../../core/ServerApiLayer/VOne/auth/choose_role";
import {
    SetFromApiDataType,
    wipeUserSessionData,
} from "../../core/LocalStorage/AuthSession/core";

// Language Context
import { useLanguage } from "../../core/translations/LanguageContext";

// Translations
import { LoginPageTranslations } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { SupportedLanguage } from "../../core/translations/supportedLanguages";

const TRANSLATIONS: Record<SupportedLanguage, LoginPageTranslations> = {
    en: enTranslations,
    de: deTranslations,
};

/**
 * Props for the LoginPage component
 */
export interface LoginPageProps {
    setSessionData: SetFromApiDataType;
    loginApiCall: LoginUserType;
    chooseRoleApiCall: ChooseRoleType;
}

/**
 * LoginPage component
 * Displays a 404 error page when a route doesn't exist
 * Provides a way to navigate back to the home page
 */
const LoginPage: React.FC<LoginPageProps> = ({
    setSessionData,
    loginApiCall,
    chooseRoleApiCall,
}) => {
    const { language, setLanguage } = useLanguage();
    //@ts-ignore
    const translations = TRANSLATIONS[language];

    // useNavigate is a hook from react-router-dom that allows us to navigate to different pages
    const navigate = useNavigate();

    // define the state for the login form
    const [login, setLogin] = useState<boolean>(true);

    // When a user is an admin in more than one place, login does not scope the
    // session to a role; we hold those admin roles here and show the picker
    // until they choose one.
    const [pendingRoles, setPendingRoles] = useState<{
        jwt: string;
        refreshTokenId: string;
        roles: AdminRole[];
    } | null>(null);

    // Set when the credentials were good but the account holds no admin role.
    const [denied, setDenied] = useState<boolean>(false);

    // define the state for the form data (will be passed into the login API call)
    const [formStatus, setFormStatus] = useState<FormData>({
        email: "",
        password: "",
        success: false,
        failure: false,
        error: "",
        loading: false,
    });

    // Exchange the unscoped session login handed back for one scoped to a single
    // admin role, then continue to /home.
    const scopeSessionToRole = async (
        jwt: string,
        refreshTokenId: string,
        role: AdminRole,
    ) => {
        const response = await chooseRoleApiCall(
            jwt,
            refreshTokenId,
            role.roleName,
            role.entityType,
            role.entityId,
        );

        if (response.status === 200 && response.data) {
            setSessionData(
                response.data.user_session,
                response.data.jwt,
                response.data.refresh_token,
            );
            setPendingRoles(null);
            navigate("/home");
        } else {
            setFormStatus((prev) => ({
                ...prev,
                loading: false,
                failure: true,
                error: response.error ?? "Could not select role",
            }));
        }
    };

    // handle the form submit event by calling the backend to login
    const handleFormSubmit = async (userData: FormData) => {
        // set the form to loading while the request is being made as hashing the password can take a while
        setFormStatus({ ...userData, loading: true });

        // login the user
        const response = await loginApiCall(userData.email, userData.password);
        if (response.status === 200 && response.data) {
            const loginResponse: LoginResponse = response.data;

            // Login is shared with the game, so it hands back every role the
            // user holds — including teacher and student roles, which mean
            // nothing here. Only their admin roles decide what happens next.
            const access = resolveAdminAccess(loginResponse);

            if (access.kind === "denied") {
                // The credentials were good, but they are not an admin. Don't
                // leave a half-live session behind for the rest of the app to
                // find.
                wipeUserSessionData();
                setDenied(true);
                setFormStatus({
                    ...userData,
                    success: false,
                    failure: false,
                    error: "",
                    loading: false,
                });
                return null;
            }

            // store the token in the local storage (unscoped while an admin of
            // several places picks one, or already role-scoped otherwise)
            setSessionData(
                loginResponse.user_session,
                loginResponse.jwt,
                loginResponse.refresh_token,
            );

            if (access.kind === "choose") {
                setPendingRoles({
                    jwt: loginResponse.jwt,
                    refreshTokenId: loginResponse.user_session.refresh_token_id,
                    roles: access.roles,
                });
                return null;
            }

            if (access.kind === "select") {
                // They hold exactly one admin role, so there is no choice to
                // offer — scope the session to it on their behalf.
                await scopeSessionToRole(
                    loginResponse.jwt,
                    loginResponse.user_session.refresh_token_id,
                    access.role,
                );
                return null;
            }

            setFormStatus({
                ...userData,
                success: true,
                failure: false,
                error: "",
            });
            navigate("/home");
            return null;
        }
        // the initial login request failed
        else {
            // A 503 is the maintenance gate, not a rejected credential — the
            // backend refuses correct passwords while a window is open.
            // @ts-ignore
            setFormStatus({
                ...userData,
                success: false,
                failure: true,
                error:
                    response.status === 503
                        ? translations.maintenanceClosed
                        : (response.error ?? ""),
                loading: false,
            });
            return null;
        }
    };

    // Scope the session to the role the user picked, then continue to /home.
    const handleRoleSelect = async (role: AdminRole) => {
        if (!pendingRoles) return;

        await scopeSessionToRole(
            pendingRoles.jwt,
            pendingRoles.refreshTokenId,
            role,
        );
    };

    return (
        <FormContainer style={{ backgroundImage: `url(outside.jpg)` }}>
            <LanguageSectionWrapper>
                <LanguageHeaderEmptyArea></LanguageHeaderEmptyArea>
            </LanguageSectionWrapper>

            <LeftPanel>
                {/* Inside the panel, not above it: FormContainer is a fixed
                    100vh with overflow hidden, so a strip above would crop the
                    form. */}
                <MaintenanceBanner />
                <LeftPanelInsideContainer>
                    <LogoImage
                        src={logoImage}
                        alt="description"
                        onClick={() => navigate("/")}
                    />
                    {!formStatus.success &&
                        !formStatus.loading &&
                        !pendingRoles &&
                        !denied && <Title>{translations.title}</Title>}
                    <FormSection>
                        {denied ? (
                            <NoPermission
                                onBack={() => {
                                    setDenied(false);
                                    setFormStatus({
                                        email: "",
                                        password: "",
                                        success: false,
                                        failure: false,
                                        error: "",
                                        loading: false,
                                    });
                                }}
                            />
                        ) : pendingRoles ? (
                            <RolePicker
                                roles={pendingRoles.roles}
                                title={translations.chooseRole.title}
                                subtitle={translations.chooseRole.subtitle}
                                onSelect={handleRoleSelect}
                            />
                        ) : (
                            <LoginForm
                                onSubmit={handleFormSubmit}
                                initialFormData={formStatus}
                            />
                        )}
                    </FormSection>
                </LeftPanelInsideContainer>
            </LeftPanel>
            <RightPanel>{/*    */}</RightPanel>
            <ModalRenderer />
        </FormContainer>
    );
};

export default LoginPage;
